/**
 * Pure helpers for SageEditModal (kept in a sibling .ts file per the
 * react-refresh/only-export-components convention, and for direct unit
 * testing without rendering).
 */

import type {
  DetectedPricingSource,
  LineItemPricingContext,
  PricingSourceOption,
} from '../../services/portalOrders';
import { findWarehouseByCode } from '../../data/sageWarehouses';

/** The order's ship_to_address JSONB shape (same keys the draft flow writes). */
export interface ShipToJson {
  ship_to_code?: string;
  ship_to_name?: string;
  address1?: string;
  address2?: string;
  address3?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  warehouse_code?: string;
}

/** "001 — Naperville, IL" style one-liner for a ship-to. */
export const shipToLabel = (
  st: { ship_to_code?: string; city?: string; state?: string } | null | undefined,
): string => {
  if (!st || !st.ship_to_code) return '—';
  const loc = [st.city, st.state].filter(Boolean).join(', ');
  return loc ? `${st.ship_to_code} — ${loc}` : st.ship_to_code;
};

/**
 * "003 — Solon, OHIO" for a known Sage warehouse code, the bare code for one
 * the hardcoded list does not know. Used in the Edit-in-Sage pending-changes
 * preview and persisted on the sage_writes audit row (changes[].old/new) so
 * the history reads like the UI did.
 */
export const warehouseLabel = (code: string): string => {
  const w = findWarehouseByCode(code);
  return w ? `${w.code} — ${w.label}` : code;
};

/**
 * Parcel-vs-LTL boundary in pounds, or `null` while no cutoff is agreed.
 *
 * DELIBERATELY UNSET (Scott, review of PR #1841): ChemMasters has not yet
 * settled its real parcel/LTL cutoff, so no threshold is assumed and the
 * crossing advisory stays OFF — the weight before→after readout still shows,
 * it just never claims an edit changed the freight mode. Guessing the common
 * ~150 lb industry figure here would put an authoritative-looking shipping
 * claim in front of CSRs on every edit.
 *
 * To turn the advisory on once the business confirms the number, set this to
 * that number — nothing else needs to change.
 */
export const PARCEL_LTL_ADVISORY_LBS: number | null = null;

/**
 * Detect an edit crossing the parcel/LTL weight boundary.
 *
 * Returns a user-facing advisory when the staged total weight moves from one
 * side of the threshold to the other (in either direction), else null.
 * Returns null whenever the threshold is unset, which is the current default —
 * see {@link PARCEL_LTL_ADVISORY_LBS}.
 */
export const weightThresholdAdvisory = (
  beforeLbs: number | null | undefined,
  afterLbs: number | null | undefined,
  thresholdLbs: number | null = PARCEL_LTL_ADVISORY_LBS,
): string | null => {
  if (thresholdLbs == null) return null;
  if (beforeLbs == null || afterLbs == null) return null;
  if (beforeLbs >= thresholdLbs && afterLbs < thresholdLbs) {
    return (
      `Weight drops below ~${thresholdLbs} lb — this shipment may no longer ` +
      'need LTL freight (parcel/small-package may apply). Re-check the ship-via.'
    );
  }
  if (beforeLbs < thresholdLbs && afterLbs >= thresholdLbs) {
    return (
      `Weight rises above ~${thresholdLbs} lb — this shipment may no longer ` +
      'qualify for parcel/small-package and may need LTL freight. Re-check the ship-via.'
    );
  }
  return null;
};

/**
 * True when two staged/saved field strings mean the same value.
 *
 * Saved qty/price come back from the API with trailing zeros ("102.8500");
 * a picker pick or a retyped value is the bare number ("102.85"). A plain
 * string compare would stage a phantom "102.8500 → 102.85" change (seen on
 * staging AU01981, 2026-09-07). Numeric strings compare as numbers; anything
 * else (blank, non-numeric) compares as trimmed text.
 */
export const sameFieldValue = (a: string, b: string): boolean => {
  const ta = a.trim();
  const tb = b.trim();
  if (ta === tb) return true;
  if (ta === '' || tb === '') return false;
  const na = Number(ta);
  const nb = Number(tb);
  return Number.isFinite(na) && Number.isFinite(nb) && na === nb;
};

// ── Pricing-source picker on a STAGED (unsaved) price ──

/**
 * Re-point a line's pricing context at the price the operator has staged.
 *
 * The backend classifies `current_source` / `available_sources[].is_active`
 * against the SAVED unit_price. In the draft editor that is always current
 * (every pick PATCHes and refetches). In Edit-in-Sage the pick is only
 * staged locally until the push, so without this the badge would keep
 * naming the old source after the operator picked a quote.
 *
 * - Staged price equals the saved price → the server view is returned as-is
 *   (keeps tier labels etc. the client can't rebuild).
 * - Staged price matches one option → that option becomes current/active.
 * - Staged price matches nothing (typed by hand) → `custom`, nothing active.
 * - Blank / non-numeric staged price → `none`, nothing active.
 */
export const restagePricingItem = (
  item: LineItemPricingContext | undefined,
  stagedPrice: string,
): LineItemPricingContext | undefined => {
  if (!item) return undefined;
  const savedNum = item.unit_price != null ? Number(item.unit_price) : null;
  const stagedNum = stagedPrice.trim() === '' ? null : Number(stagedPrice);
  if (stagedNum != null && Number.isFinite(stagedNum) && savedNum === stagedNum) {
    return item;
  }
  const options = item.available_sources ?? [];
  const isMatch = (opt: PricingSourceOption): boolean =>
    stagedNum != null && Number.isFinite(stagedNum) && opt.price === stagedNum;
  const matched = options.find(isMatch) ?? null;
  const current_source: DetectedPricingSource = matched
    ? {
        type: matched.type,
        quote_source_id: matched.quote_source_id,
        tier_label: matched.type === 'tiered' ? matched.label : null,
        tier_price: matched.type === 'tiered' ? matched.price : null,
        // The base-discount % and list live on the line context (the
        // option row only carries the price), so re-read them from there.
        ...(matched.type === 'base_discount'
          ? {
              base_discount_pct: item.applied_base_discount_pct ?? null,
              list_price:
                item.list_price_before_discount != null
                  ? Number(item.list_price_before_discount)
                  : null,
            }
          : {}),
        // The promotion label lives on the line context too.
        ...(matched.type === 'promotion'
          ? {
              promotion_label: item.promotion?.label ?? null,
              promotion_price: matched.price,
            }
          : {}),
      }
    : {
        type: stagedNum == null || !Number.isFinite(stagedNum) ? 'none' : 'custom',
        quote_source_id: null,
        tier_label: null,
        tier_price: null,
      };
  return {
    ...item,
    current_source,
    available_sources: options.map((opt) => ({ ...opt, is_active: isMatch(opt) })),
  };
};

// ── Per-line Sage comment (append-only) ──

/** What sending `addition` would actually do to a Sage line's CommentText. */
export type SageCommentOutcome =
  /** Nothing typed — the field is not sent and Sage is untouched. */
  | { effect: 'none'; result: string }
  /** The line had no comment; the text is set outright. */
  | { effect: 'set'; result: string }
  /** Appended after the existing comment with " | ". */
  | { effect: 'append'; result: string }
  /** Already contained in the existing comment — Sage silently ignores it. */
  | { effect: 'already_present'; result: string };

/**
 * Mirror of Adam's `BOI::updateSalesOrderLine` comment handling, so the
 * operator sees the real outcome before pushing rather than after.
 *
 * His logic, verified end-to-end against real Sage 100 on the sandbox
 * 2026-09-09 (14/14, kanban 099373a1): read the line's current
 * `CommentText$`; if the new text is blank leave it alone; if the current
 * value is empty set the new text; if the new text is ALREADY CONTAINED in
 * the current value do nothing (and still report success — the trap this
 * function exists to surface); otherwise write `existing | new`.
 *
 * There is no replace and no clear. A comment already in Sage can only be
 * added to.
 */
/** Sage's SO_SalesOrderDetail.CommentText capacity; boi.php substr()s to it. */
export const SAGE_LINE_COMMENT_MAX_LEN = 2048;

export const sageCommentOutcome = (
  existing: string,
  addition: string,
): SageCommentOutcome => {
  const cur = existing.trim();
  const add = addition.trim();
  if (add === '') return { effect: 'none', result: cur };
  if (cur === '') return { effect: 'set', result: add.slice(0, SAGE_LINE_COMMENT_MAX_LEN) };
  if (cur.includes(add)) return { effect: 'already_present', result: cur };
  // boi.php writes substr($combined, 0, 2048): a long existing comment can
  // swallow the tail of the addition. Preview what will actually land.
  return { effect: 'append', result: `${cur} | ${add}`.slice(0, SAGE_LINE_COMMENT_MAX_LEN) };
};

// ── Staged-order guard (kanban 42a2ed0a) ────────────────────────────────────

/** Header fields whose change alters what leaves the dock or how. Mirrors
 *  SHIPMENT_HEADER_FIELDS in services/orders/staged_edit_guard.py. */
const SHIPMENT_HEADER_FIELDS = new Set(['ship_via', 'ship_to', 'warehouse_code', 'udf_bl', 'requested_ship_date']);
const SHIPMENT_LINE_KINDS: Record<string, string> = {
  line_qty: 'line quantity',
  line_add: 'line added',
  line_remove: 'line removed',
};

/**
 * The parts of a staged edit the dock has to know about, in display order.
 * Empty means the edit is money / paperwork only (price, Sage comment, PO)
 * and the staged-order override is not needed. Same rule as the server's
 * shipment_affecting_changes, so the modal never prompts for an edit the
 * server would let through, or vice versa.
 */
export const shipmentAffectingChanges = (
  changes: ReadonlyArray<{ field: string; kind?: string | null }>,
): string[] => {
  const out: string[] = [];
  for (const c of changes) {
    const label =
      c.kind === 'field' && SHIPMENT_HEADER_FIELDS.has(c.field)
        ? c.field
        : c.kind
          ? SHIPMENT_LINE_KINDS[c.kind]
          : undefined;
    if (label && !out.includes(label)) out.push(label);
  }
  return out;
};

/** "Thu 10/16 6:12 AM" in the viewer's zone; null when no stamp. */
export const stagedSinceLabel = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    weekday: 'short', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

/** "10/16" from an ISO date; null when unset. */
export const shortDateLabel = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  return `${Number(m[2])}/${Number(m[3])}`;
};
